import { useCallback, useEffect, useState } from "react";

/**
 * Shared, persisted "which hospital am I looking at" selection.
 *
 * Multi-site accounts pick a hospital on one page (Live Activity, Devices,
 * Calendar, Reports, …) and reasonably expect that choice to follow them from
 * page to page until they change it themselves. Previously each page kept its
 * own useState, so the selection reset to "All Hospitals" on every navigation.
 *
 * This hook is a drop-in replacement for that `useState<string | undefined>`:
 * it reads and writes a single localStorage key, so every page shares one
 * selection, and it broadcasts changes on a window event so a page that is
 * already mounted updates live (and it also honours the native `storage` event
 * so a second browser tab stays in sync). `undefined` (or empty) means
 * "All Hospitals".
 *
 * `fallback` is only used when nothing has been chosen yet — it preserves each
 * page's original default (e.g. a role that is pinned to its own hospital).
 */
const STORAGE_KEY = "guardai.selectedHospitalId";
const CHANGE_EVENT = "guardai:hospitalfilter";

function readStored(): string | undefined {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? v : undefined;
  } catch {
    return undefined;
  }
}

export function useStickyHospital(
  fallback?: string
): [string | undefined, (next: string | undefined) => void] {
  const [value, setValue] = useState<string | undefined>(() => readStored() ?? fallback);

  useEffect(() => {
    const sync = () => setValue(readStored() ?? fallback);
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [fallback]);

  const set = useCallback((next: string | undefined) => {
    try {
      if (next) localStorage.setItem(STORAGE_KEY, next);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable — keep working in-memory for this page */
    }
    setValue(next || undefined);
    try {
      window.dispatchEvent(new Event(CHANGE_EVENT));
    } catch {
      /* no-op */
    }
  }, []);

  return [value, set];
}
